import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faShoppingCart, faEye, faCalendarAlt, faDollarSign, faCrown, faClock, faCheckCircle, faTimesCircle, faPause, faPlay, faUserCheck, faChartLine, faFilter, faSearch, faChartBar, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useStrictModeGuard } from '../hooks/useStrictModeGuard';
import { User, Purchase } from '../types';
import Pagination from './Pagination';

interface ClientUser {
  email: string;
  name?: string;
  purchase_count: number;
  total_spent: number;
  last_purchase: string;
  subscription_status: 'active' | 'inactive';
  purchases?: Purchase[];
}

interface SubscriptionStats {
  totalSubscribers: number;
  activeSubscriptions: number;
  totalRevenue: number;
  monthlyRecurring: number;
}

const UserPurchases: React.FC = () => {
  const { executeOnce } = useStrictModeGuard();
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ClientUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [stats, setStats] = useState<SubscriptionStats>({
    totalSubscribers: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    monthlyRecurring: 0
  });
  const [showPlanDetailsModal, setShowPlanDetailsModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
  const [loadingPurchases, setLoadingPurchases] = useState<string | null>(null);
  const [isEditingSubscription, setIsEditingSubscription] = useState(false);
  const [editingFeatures, setEditingFeatures] = useState<string[]>([]);
  const [editingConsumableCounts, setEditingConsumableCounts] = useState<{[key: string]: number}>({});
  const [availableFeatures, setAvailableFeatures] = useState<any[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);

  const fetchUsers = useCallback(async (searchQuery?: string) => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const params = {
        skip,
        limit: itemsPerPage,
        ...(searchQuery ? { search: searchQuery } : {})
      };
      const response = await axios.get('/api/v1/client-users', { params });
      const responseData = response.data;
      const usersData = responseData.items || responseData; // Handle both old and new format
      
      // Update pagination info from response
      if (responseData.total_count !== undefined) {
        setTotalItems(responseData.total_count);
        setTotalPages(responseData.total_pages);
      }
      
      // Set users without fetching purchases initially
      setUsers(usersData);
      
      // Calculate stats (only for current page data)
      const totalSubscribers = usersData.length;
      const activeSubscriptions = usersData.filter((user: ClientUser) => user.subscription_status === 'active').length;
      const totalRevenue = usersData.reduce((sum: number, user: ClientUser) => sum + user.total_spent, 0);
      
      setStats({
        totalSubscribers,
        activeSubscriptions,
        totalRevenue,
        monthlyRecurring: 0 // Will be calculated when purchases are loaded
      });
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch subscribers');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchUsers(searchTerm || undefined);
  }, [currentPage, searchTerm, fetchUsers]);

  const fetchUserPurchases = async (userEmail: string) => {
    try {
      setLoadingPurchases(userEmail);
      const purchasesResponse = await axios.get(`/api/v1/client-users/${encodeURIComponent(userEmail)}/purchases`);
      const purchases = purchasesResponse.data;
      
      // Update the user in the users array with their purchases
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.email === userEmail 
            ? { ...user, purchases }
            : user
        )
      );
      
      // Update monthly recurring revenue with the new purchase data
      const monthlyRecurring = purchases?.filter((p: Purchase) => 
        p.status === 'completed' && p.duration?.includes('month')
      ).reduce((sum: number, p: Purchase) => sum + (parseFloat(p.amount || '0')), 0) || 0;
      
      setStats(prevStats => ({
        ...prevStats,
        monthlyRecurring: prevStats.monthlyRecurring + monthlyRecurring
      }));
      
    } catch (error) {
      console.error(`Error fetching purchases for user ${userEmail}:`, error);
      // Set empty purchases array on error
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.email === userEmail 
            ? { ...user, purchases: [] }
            : user
        )
      );
    } finally {
      setLoadingPurchases(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (purchase: Purchase) => {
    const now = new Date();
    const isActive = isSubscriptionActive(purchase);
    
    if (purchase.status === 'completed' && isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-1.5 w-3 h-3" />
          Active
        </span>
      );
    } else if (purchase.status === 'completed' && !isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <FontAwesomeIcon icon={faPause} className="mr-1.5 w-3 h-3" />
          Expired
        </span>
      );
    } else if (purchase.status === 'pending') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <FontAwesomeIcon icon={faClock} className="mr-1.5 w-3 h-3" />
          Pending
        </span>
      );
    } else if (purchase.status === 'failed') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <FontAwesomeIcon icon={faTimesCircle} className="mr-1.5 w-3 h-3" />
          Failed
        </span>
      );
    } else if (purchase.status === 'upgraded') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <FontAwesomeIcon icon={faArrowUp} className="mr-1.5 w-3 h-3" />
          Upgraded
        </span>
      );
    } else if (purchase.status === 'cancelled') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <FontAwesomeIcon icon={faPause} className="mr-1.5 w-3 h-3" />
          Cancelled
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <FontAwesomeIcon icon={faPause} className="mr-1.5 w-3 h-3" />
          {purchase.status || 'Unknown'}
        </span>
      );
    }
  };

  const isUserActive = (user: ClientUser) => {
    return user.subscription_status === 'active';
  };

  const isSubscriptionActive = (purchase: Purchase) => {
    if (purchase.status !== 'completed') return false;
    
    // If no end_date, consider it active if status is completed
    if (!purchase.end_date) return true;
    
    // Check if subscription is still within the date range
    const now = new Date();
    const endDate = new Date(purchase.end_date);
    return endDate > now;
  };

  const getFilteredUsers = () => {
    // Apply status filter only (search is handled by backend)
    switch (filter) {
      case 'active':
        return users.filter(isUserActive);
      case 'inactive':
        return users.filter(user => !isUserActive(user));
      default:
        return users;
    }
  };

  const handleSearch = async (query: string) => {
    setSearchTerm(query);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleShowPlanDetails = (purchase: Purchase) => {
    console.log('Selected purchase:', purchase);
    console.log('Application data:', purchase.application);
    console.log('Application ID:', purchase.application?.id);
    setSelectedPurchase(purchase);
    setShowPlanDetailsModal(true);
  };

  const handleClosePlanDetailsModal = () => {
    setShowPlanDetailsModal(false);
    setSelectedPurchase(null);
    setIsEditingSubscription(false);
    setEditingFeatures([]);
    setEditingConsumableCounts({});
    setAvailableFeatures([]);
    setError(null); // Clear any errors when closing modal
  };

  const handleEditSubscription = async () => {
    if (!selectedPurchase?.application?.id) {
      setError('No application information available for this subscription');
      return;
    }
    
    try {
      setLoadingFeatures(true);
      setError(null); // Clear any previous errors
      
      console.log('Fetching features for application:', selectedPurchase.application.id);
      console.log('Selected purchase data:', selectedPurchase);
      
      // Try multiple approaches to get all application features
      let allFeatures: any[] = [];
      
      try {
        // First try: Get application features directly
        console.log(`Attempting to fetch features for application ${selectedPurchase.application.id}...`);
        const featuresResponse = await axios.get(`/applications/${selectedPurchase.application.id}/features`);
        console.log('Features response:', featuresResponse.data);
        
        if (featuresResponse.data?.features && featuresResponse.data.features.length > 0) {
          allFeatures = featuresResponse.data.features;
          console.log(`✅ Successfully loaded ${allFeatures.length} features from dedicated features endpoint`);
        } else {
          console.log('⚠️ No features found via dedicated endpoint');
        }
      } catch (featuresError: any) {
        console.log('❌ Failed to get features via dedicated endpoint:', featuresError.response?.status, featuresError.response?.data);
        
        // Fallback: Try to get application with features
        try {
          console.log('Trying fallback: application endpoint with joinedload...');
          const response = await axios.get(`/applications/${selectedPurchase.application.id}`);
          const application = response.data;
          console.log('Application data received:', application);
          console.log('Features from application:', application.features);
          
          if (application.features && application.features.length > 0) {
            allFeatures = application.features;
            console.log(`✅ Successfully loaded ${allFeatures.length} features from application endpoint`);
          } else {
            console.log('⚠️ Application has no features defined');
          }
        } catch (appError: any) {
          console.log('❌ Failed to get application features:', appError.response?.status, appError.response?.data);
          console.log('Trying alternative approach...');
        }
      }
      
      // If we didn't get features from application endpoint, try to get them from plans
      if (allFeatures.length === 0) {
        try {
          console.log(`Attempting to fetch plans for application ${selectedPurchase.application.id}...`);
          const plansResponse = await axios.get(`/plans?application_id=${selectedPurchase.application.id}`);
          console.log('Plans response:', plansResponse.data);
          
          // Extract all unique features from all plans
          const allPlanFeatures = new Set<string>();
          const allPlanConsumableFeatures = new Set<string>();
          
          if (plansResponse.data?.items && plansResponse.data.items.length > 0) {
            console.log(`📊 Found ${plansResponse.data.items.length} plans for application`);
            console.log('📋 Full plans response:', plansResponse.data);
            
            plansResponse.data.items.forEach((plan: any, index: number) => {
              console.log(`📝 Plan ${index + 1}: "${plan.name}" (ID: ${plan.id})`);
              console.log('   - selected_features:', plan.selected_features);
              console.log('   - consumable_counts:', plan.consumable_counts);
              console.log('   - Full plan data:', plan);
              
              // Add features from selected_features
              if (plan.selected_features && Array.isArray(plan.selected_features)) {
                plan.selected_features.forEach((feature: string) => {
                  allPlanFeatures.add(feature);
                  console.log(`   ✅ Added feature from selected_features: ${feature}`);
                });
              } else {
                console.log('   ⚠️ No selected_features or not an array');
              }
              
              // Add features from consumable_counts
              if (plan.consumable_counts && typeof plan.consumable_counts === 'object') {
                Object.keys(plan.consumable_counts).forEach((feature: string) => {
                  allPlanFeatures.add(feature);
                  allPlanConsumableFeatures.add(feature);
                  console.log(`   ✅ Added consumable feature: ${feature} (count: ${plan.consumable_counts[feature]})`);
                });
              } else {
                console.log('   ⚠️ No consumable_counts or not an object');
              }
            });
            
            console.log(`🎯 FINAL RESULT: Extracted ${allPlanFeatures.size} unique features from ${plansResponse.data.items.length} plans`);
            console.log('📋 All unique features:', Array.from(allPlanFeatures));
            console.log('📋 All consumable features:', Array.from(allPlanConsumableFeatures));
          } else {
            console.log('⚠️ No plans found for this application');
            console.log('📋 Plans response data:', plansResponse.data);
          }
          
          // Create feature objects from plan data
          if (allPlanFeatures.size > 0) {
            allFeatures = Array.from(allPlanFeatures).map((featureCode: string) => ({
              feature_code: featureCode,
              name: featureCode.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              description: `Feature: ${featureCode}`,
              is_consumable: allPlanConsumableFeatures.has(featureCode)
            }));
            
            console.log(`✅ SUCCESS: Created ${allFeatures.length} feature objects from plans`);
            console.log('🎯 Final feature objects:', allFeatures);
            
            // Log each feature for verification
            allFeatures.forEach((feature, index) => {
              console.log(`   ${index + 1}. ${feature.feature_code} (${feature.is_consumable ? 'consumable' : 'non-consumable'})`);
            });
          } else {
            console.log('⚠️ No features could be extracted from plans - allPlanFeatures is empty');
          }
        } catch (plansError: any) {
          console.log('❌ Failed to get features from plans:', plansError.response?.status, plansError.response?.data);
        }
      }
      
      // Final fallback: Use only current plan features (no assumed features)
      if (allFeatures.length === 0) {
        console.log('Using minimal fallback: only current plan features');
        
        const currentFeatures = selectedPurchase.purchased_plan?.selected_features || [];
        const currentConsumableCounts = selectedPurchase.purchased_plan?.consumable_counts || {};
        
        // Only use features that actually exist in the current plan
        const allFeatureCodes = new Set([
          ...currentFeatures,
          ...Object.keys(currentConsumableCounts)
        ]);
        
        allFeatures = Array.from(allFeatureCodes).map((featureCode: string) => ({
          feature_code: featureCode,
          name: featureCode.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          description: `Feature: ${featureCode}`,
          is_consumable: currentConsumableCounts.hasOwnProperty(featureCode)
        }));
        
        console.log('Created minimal fallback features from current plan only:', allFeatures);
      }
      
      setAvailableFeatures(allFeatures);
      
      // Initialize editing state with current values
      const currentFeatures = selectedPurchase.purchased_plan?.selected_features || [];
      const currentConsumableCounts = selectedPurchase.purchased_plan?.consumable_counts || {};
      
      setEditingFeatures([...currentFeatures]);
      setEditingConsumableCounts({...currentConsumableCounts});
      setIsEditingSubscription(true);
      
      if (allFeatures.length > 0) {
        setError(null); // Clear error if we got features
        console.log(`Successfully loaded ${allFeatures.length} features for editing`);
      } else {
        setError('Unable to load application features - please check application configuration');
        console.log('No features could be loaded from any source');
      }
      
    } catch (error: any) {
      console.error('Error in handleEditSubscription:', error);
      setError('Failed to load application features');
      setIsEditingSubscription(false);
    } finally {
      setLoadingFeatures(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingSubscription(false);
    setEditingFeatures([]);
    setEditingConsumableCounts({});
  };

  const handleSaveSubscription = async () => {
    if (!selectedPurchase) return;
    
    try {
      setUpdatingSubscription(true);
      
      const updateData: any = {};
      
      // Ensure all consumable features are included in selected_features
      const allSelectedFeatures = new Set(editingFeatures);
      Object.keys(editingConsumableCounts).forEach(featureCode => {
        allSelectedFeatures.add(featureCode);
      });
      const finalSelectedFeatures = Array.from(allSelectedFeatures);
      
      // Only send features if they changed
      const currentFeatures = selectedPurchase.purchased_plan?.selected_features || [];
      if (JSON.stringify(finalSelectedFeatures.sort()) !== JSON.stringify(currentFeatures.sort())) {
        updateData.selected_features = finalSelectedFeatures;
      }
      
      // Only send consumable counts if they changed
      const currentConsumableCounts = selectedPurchase.purchased_plan?.consumable_counts || {};
      if (JSON.stringify(editingConsumableCounts) !== JSON.stringify(currentConsumableCounts)) {
        updateData.consumable_counts = editingConsumableCounts;
      }
      
      if (Object.keys(updateData).length === 0) {
        setIsEditingSubscription(false);
        return;
      }
      
      await axios.put(`/api/v1/purchases/${selectedPurchase.transaction_id}/subscription`, updateData);
      
      // Update the selected purchase with new data
      const updatedPurchase: Purchase = {
        ...selectedPurchase,
        purchased_plan: selectedPurchase.purchased_plan ? {
          ...selectedPurchase.purchased_plan,
          selected_features: finalSelectedFeatures,
          consumable_counts: editingConsumableCounts
        } : undefined
      };
      setSelectedPurchase(updatedPurchase);
      
      // Update the user's purchases in the list
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.email === selectedPurchase.user_email 
            ? {
                ...user,
                purchases: user.purchases?.map(p => 
                  p.transaction_id === selectedPurchase.transaction_id 
                    ? updatedPurchase 
                    : p
                ) || []
              }
            : user
        )
      );
      
      setIsEditingSubscription(false);
    } catch (error: any) {
      console.error('Error updating subscription:', error);
      setError(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleFeatureToggle = (featureCode: string) => {
    setEditingFeatures(prev => {
      if (prev.includes(featureCode)) {
        // Remove feature and its consumable count if it exists
        const newConsumableCounts = { ...editingConsumableCounts };
        delete newConsumableCounts[featureCode];
        setEditingConsumableCounts(newConsumableCounts);
        return prev.filter(f => f !== featureCode);
      } else {
        // Add feature
        const newFeatures = [...prev, featureCode];
        
        // If it's a consumable feature, set a default limit
        const feature = availableFeatures.find(f => f.feature_code === featureCode);
        if (feature?.is_consumable) {
          const currentLimit = editingConsumableCounts[featureCode] || 100; // Default to 100
          setEditingConsumableCounts(prev => ({
            ...prev,
            [featureCode]: currentLimit
          }));
        }
        
        return newFeatures;
      }
    });
  };

  const handleConsumableCountChange = (featureCode: string, count: number) => {
    setEditingConsumableCounts(prev => ({
      ...prev,
      [featureCode]: count
    }));
  };

  const getSubscriptionType = (purchase: Purchase) => {
    if (purchase.duration?.includes('year')) return 'Annual';
    if (purchase.duration?.includes('month')) return 'Monthly';
    if (purchase.duration?.includes('week')) return 'Weekly';
    return 'One-time';
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscribers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faEye} className="text-red-500 text-xl" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const filteredUsers = getFilteredUsers();

  return (
    <div>
      {/* Welcome Banner */}
      <div className="welcome-banner">
        <h1 className="welcome-title">Subscriber Management</h1>
        <p className="welcome-subtitle">Manage your application's subscribers and their subscription plans</p>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-trend trend-up">
            ↑ +{((stats.activeSubscriptions / Math.max(stats.totalSubscribers, 1)) * 100).toFixed(1)}%
          </div>
          <div className="metric-icon" style={{ backgroundColor: '#e0f2fe', color: '#0ea5e9' }}>
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="metric-value">{stats.totalSubscribers}</div>
          <div className="metric-label">Total Subscribers</div>
        </div>

        <div className="metric-card">
          <div className="metric-trend trend-up">
            ↑ +{((stats.activeSubscriptions / Math.max(stats.totalSubscribers, 1)) * 100).toFixed(1)}%
          </div>
          <div className="metric-icon" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="metric-value">{stats.activeSubscriptions}</div>
          <div className="metric-label">Active Subscriptions</div>
        </div>

        <div className="metric-card">
          <div className="metric-trend trend-up">
            ↑ +{((stats.monthlyRecurring / Math.max(stats.totalRevenue, 1)) * 100).toFixed(1)}%
          </div>
          <div className="metric-icon" style={{ backgroundColor: '#f3e8ff', color: '#a855f7' }}>
            <FontAwesomeIcon icon={faDollarSign} />
          </div>
          <div className="metric-value">${stats.totalRevenue.toFixed(0)}</div>
          <div className="metric-label">Total Revenue</div>
        </div>

        <div className="metric-card">
          <div className="metric-trend trend-up">
            ↑ +{((stats.monthlyRecurring / Math.max(stats.totalRevenue, 1)) * 100).toFixed(1)}%
          </div>
          <div className="metric-icon" style={{ backgroundColor: '#fff7ed', color: '#f97316' }}>
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div className="metric-value">${stats.monthlyRecurring.toFixed(0)}</div>
          <div className="metric-label">Monthly Recurring</div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="card">
        <div className="section-header">
          <div className="section-icon" style={{ color: '#3b82f6' }}>
            <FontAwesomeIcon icon={faFilter} />
          </div>
          <h3 className="section-title">Search & Filter Subscribers</h3>
        </div>
        
        {/* Search Input */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ position: 'relative' }}>
            <FontAwesomeIcon 
              icon={faSearch} 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#6b7280',
                fontSize: '14px'
              }} 
            />
            <input
              type="text"
              placeholder="Search by email, name, app name, plan name, or transaction ID..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: '#fff',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
              }}
            />
          </div>
          {searchTerm && (
            <div style={{ 
              marginTop: '8px', 
              fontSize: '12px', 
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>Searching for: "{searchTerm}"</span>
              <button
                onClick={() => handleSearch('')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: '12px',
                  textDecoration: 'underline'
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
        
        {/* Filter Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All', count: users.length },
            { key: 'active', label: 'Active', count: users.filter(isUserActive).length },
            { key: 'inactive', label: 'Inactive', count: users.filter(user => !isUserActive(user)).length }
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key as any)}
              className={`btn ${filter === key ? 'btn-primary' : 'btn-secondary'}`}
              style={{ marginRight: '8px' }}
            >
              {label} ({count})
            </button>
          ))}
          <div style={{ color: '#6b7280', fontSize: '14px', marginLeft: 'auto' }}>
            Showing {filteredUsers.length} of {totalItems} subscribers
          </div>
        </div>
      </div>

      {/* Subscribers List */}
      {filteredUsers.length === 0 ? (
        <div className="card text-center">
          <div style={{ color: '#6b7280', fontSize: '14px' }}>
            {searchTerm ? (
              `No subscribers found matching "${searchTerm}". Try adjusting your search terms or filters.`
            ) : filter === 'all' ? (
              'Subscribers will appear here once customers start purchasing plans from your applications.'
            ) : (
              `No ${filter} subscribers found. Try changing the filter to see more results.`
            )}
          </div>
        </div>
      ) : (
        <div className="dashboard-grid">
          {filteredUsers.map((user) => (
            <div key={user.email} className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: isUserActive(user) ? '#16a34a' : '#6b7280' }}>
                  <FontAwesomeIcon icon={faUserCheck} />
                </div>
                <h3 className="section-title">{user.name || 'Unknown User'}</h3>
                <div style={{ marginLeft: 'auto' }}>
                  {isUserActive(user) ? (
                    <span className="status-badge status-active">Active</span>
                  ) : (
                    <span className="status-badge status-inactive">Inactive</span>
                  )}
                </div>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>{user.email}</div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ 
                    backgroundColor: '#f3e8ff', 
                    color: '#a855f7', 
                    padding: '4px 8px', 
                    borderRadius: '12px', 
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {user.purchase_count} subscriptions
                  </div>
                  <div style={{ 
                    backgroundColor: '#dcfce7', 
                    color: '#166534', 
                    padding: '4px 8px', 
                    borderRadius: '12px', 
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    ${user.total_spent.toFixed(2)} lifetime value
                  </div>
                  <div style={{ 
                    backgroundColor: '#e0f2fe', 
                    color: '#0c4a6e', 
                    padding: '4px 8px', 
                    borderRadius: '12px', 
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    Last: {formatDate(user.last_purchase)}
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  if (selectedUser?.email === user.email) {
                    // Hide subscriptions
                    setSelectedUser(null);
                  } else {
                    // Show subscriptions - fetch if not already loaded
                    setSelectedUser(user);
                    if (!user.purchases) {
                      fetchUserPurchases(user.email);
                    }
                  }
                }}
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={loadingPurchases === user.email}
              >
                {loadingPurchases === user.email ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Loading...
                  </>
                ) : (
                  selectedUser?.email === user.email ? 'Hide' : 'View'
                )} Subscriptions
              </button>
              
              {/* Subscription History */}
              {selectedUser?.email === user.email && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
                  <div className="section-header" style={{ marginBottom: '16px' }}>
                    <div className="section-icon" style={{ color: '#a855f7' }}>
                      <FontAwesomeIcon icon={faCrown} />
                    </div>
                    <h4 className="section-title" style={{ fontSize: '16px' }}>Subscription History</h4>
                  </div>
                  {loadingPurchases === user.email ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <div style={{ color: '#6b7280', fontSize: '14px' }}>Loading subscription history...</div>
                    </div>
                  ) : user.purchases && user.purchases.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {user.purchases.map((purchase) => (
                        <div key={purchase.id} style={{ 
                          backgroundColor: '#f9fafb',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}>
                           <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '8px' }}>
                             <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                               {getStatusBadge(purchase)}
                               <span style={{ 
                                 backgroundColor: '#e0f2fe', 
                                 color: '#0c4a6e', 
                                 padding: '2px 6px', 
                                 borderRadius: '12px', 
                                 fontSize: '11px',
                                 fontWeight: '500'
                               }}>
                                 {getSubscriptionType(purchase)}
                               </span>
                             </div>
                           </div>
                          {/* Application and Plan Info */}
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              {purchase.application?.logo_url && (
                                <img 
                                  src={purchase.application.logo_url} 
                                  alt={`${purchase.application.name} logo`}
                                  style={{ 
                                    width: '20px', 
                                    height: '20px', 
                                    borderRadius: '4px',
                                    objectFit: 'contain'
                                  }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                              <div>
                                <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
                                  {purchase.application?.name || 'Unknown App'}
                                </div>
                                <div style={{ fontWeight: '500', color: '#6b7280', fontSize: '12px' }}>
                                  Plan: {purchase.plan?.name || 'Unknown Plan'}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px', fontSize: '12px' }}>
                            {purchase.start_date && (
                              <div>
                                <div style={{ fontWeight: '500', color: '#374151' }}>Start Date</div>
                                <div style={{ color: '#6b7280' }}>{formatDate(purchase.start_date)}</div>
                              </div>
                            )}
                            {purchase.end_date && (
                              <div>
                                <div style={{ fontWeight: '500', color: '#374151' }}>End Date</div>
                                <div style={{ color: '#6b7280' }}>{formatDate(purchase.end_date)}</div>
                              </div>
                            )}
                            {purchase.amount && purchase.currency && (
                              <div>
                                <div style={{ fontWeight: '500', color: '#374151' }}>Amount</div>
                                <div style={{ color: '#059669', fontWeight: '600' }}>{purchase.currency} {purchase.amount}</div>
                              </div>
                            )}
                            {purchase.duration && (
                              <div>
                                <div style={{ fontWeight: '500', color: '#374151' }}>Duration</div>
                                <div style={{ color: '#6b7280' }}>{purchase.duration}</div>
                              </div>
                            )}
                          </div>
                          
                          {/* Show Details Button */}
                          <div style={{ marginTop: '12px', textAlign: 'right' }}>
                            <button
                              onClick={() => handleShowPlanDetails(purchase)}
                              style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                transition: 'background-color 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#2563eb';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = '#3b82f6';
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} style={{ fontSize: '10px' }} />
                              Show Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', padding: '16px' }}>
                      No subscription history available
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {filteredUsers.length > 0 && (
        <div className="card">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            showInfo={true}
          />
        </div>
      )}

      {/* Plan Details Modal */}
      {showPlanDetailsModal && selectedPurchase && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            {/* Error Display */}
            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#dc2626', fontSize: '16px' }} />
                  <span style={{ color: '#dc2626', fontSize: '14px', fontWeight: '500' }}>
                    {error}
                  </span>
                </div>
              </div>
            )}

            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                Plan Details
              </h2>
              <button
                onClick={handleClosePlanDetailsModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                ×
              </button>
            </div>

            {/* Plan Information */}
            <div style={{ marginBottom: '24px' }}>
              {/* Plan Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                {selectedPurchase.application?.logo_url && (
                  <img 
                    src={selectedPurchase.application.logo_url} 
                    alt={`${selectedPurchase.application.name} logo`}
                    style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '8px',
                      objectFit: 'contain'
                    }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: '0 0 4px 0',
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b'
                  }}>
                    {selectedPurchase.purchased_plan?.name || selectedPurchase.plan?.name || 'Unknown Plan'}
                  </h3>
                  {selectedPurchase.application && (
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#64748b',
                      fontWeight: '500'
                    }}>
                      {selectedPurchase.application.name}
                    </div>
                  )}
                </div>
                <div style={{
                  textAlign: 'right'
                }}>
                  {selectedPurchase.amount && selectedPurchase.currency && (
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#059669'
                    }}>
                      {selectedPurchase.currency} {selectedPurchase.amount}
                    </div>
                  )}
                  {selectedPurchase.duration && (
                    <div style={{
                      fontSize: '12px',
                      color: '#64748b',
                      textTransform: 'capitalize'
                    }}>
                      {selectedPurchase.duration}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Plan Description */}
              {selectedPurchase.purchased_plan?.description && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#f1f5f9',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1'
                }}>
                  <h4 style={{
                    margin: '0 0 8px 0',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#475569',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Plan Description
                  </h4>
                  <p style={{
                    margin: 0,
                    color: '#334155',
                    lineHeight: '1.6',
                    fontSize: '15px'
                  }}>
                    {selectedPurchase.purchased_plan.description}
                  </p>
                </div>
              )}

              {/* Pricing Information */}
              {selectedPurchase.purchased_plan?.pricing_tiers && selectedPurchase.purchased_plan.pricing_tiers.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    margin: '0 0 12px 0',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Pricing Tiers
                  </h4>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {selectedPurchase.purchased_plan.pricing_tiers.map((tier: any, index: number) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: tier.duration === selectedPurchase.duration ? '#eff6ff' : '#f9fafb',
                        border: tier.duration === selectedPurchase.duration ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>
                            {tier.duration || 'One-time'}
                          </div>
                          {tier.currency && tier.amount && (
                            <div style={{ fontSize: '14px', color: '#6b7280' }}>
                              {tier.currency} {tier.amount}
                            </div>
                          )}
                        </div>
                        {tier.duration === selectedPurchase.duration && (
                          <div style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            Selected
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Features Section */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#059669', fontSize: '16px' }} />
                  {isEditingSubscription ? 'Edit Plan Features' : 'Plan Features'}
                </h4>
                
                {/* Features Display/Edit */}
                {isEditingSubscription ? (
                  <div style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <div style={{ marginBottom: '16px' }}>
                      <h5 style={{
                        margin: '0 0 12px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#475569'
                      }}>
                        Available Features
                      </h5>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        {availableFeatures.map((feature: any) => (
                          <div key={feature.feature_code} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px',
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <input
                                type="checkbox"
                                checked={editingFeatures.includes(feature.feature_code)}
                                onChange={() => handleFeatureToggle(feature.feature_code)}
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  cursor: 'pointer'
                                }}
                              />
                              <div>
                                <div style={{
                                  fontWeight: '500',
                                  color: '#374151',
                                  fontSize: '14px'
                                }}>
                                  {feature.name}
                                </div>
                                {feature.description && (
                                  <div style={{
                                    fontSize: '12px',
                                    color: '#6b7280',
                                    marginTop: '2px'
                                  }}>
                                    {feature.description}
                                  </div>
                                )}
                                <div style={{
                                  fontSize: '11px',
                                  color: feature.is_consumable ? '#d97706' : '#059669',
                                  fontWeight: '500',
                                  marginTop: '2px'
                                }}>
                                  {feature.is_consumable ? 'Consumable Feature' : 'Standard Feature'}
                                </div>
                              </div>
                            </div>
                            {feature.is_consumable && editingFeatures.includes(feature.feature_code) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label style={{
                                  fontSize: '12px',
                                  color: '#6b7280',
                                  fontWeight: '500'
                                }}>
                                  Limit:
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  max="999999"
                                  value={editingConsumableCounts[feature.feature_code] || 1}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value) || 1;
                                    handleConsumableCountChange(feature.feature_code, Math.max(1, value));
                                  }}
                                  style={{
                                    width: '80px',
                                    padding: '4px 8px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    textAlign: 'center'
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentCount = editingConsumableCounts[feature.feature_code] || 1;
                                    handleConsumableCountChange(feature.feature_code, currentCount + 10);
                                  }}
                                  style={{
                                    background: '#f3f4f6',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    color: '#374151'
                                  }}
                                >
                                  +10
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentCount = editingConsumableCounts[feature.feature_code] || 1;
                                    handleConsumableCountChange(feature.feature_code, Math.max(1, currentCount - 10));
                                  }}
                                  style={{
                                    background: '#f3f4f6',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    color: '#374151'
                                  }}
                                >
                                  -10
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Changes Summary */}
                    <div style={{ marginTop: '16px' }}>
                      <h5 style={{
                        margin: '0 0 8px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#475569'
                      }}>
                        Changes Summary
                      </h5>
                      <div style={{
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '12px',
                        fontSize: '12px'
                      }}>
                        <div style={{ marginBottom: '8px' }}>
                          <strong>Selected Features:</strong> {editingFeatures.length} features
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <strong>Consumable Features:</strong> {Object.keys(editingConsumableCounts).length} with limits
                        </div>
                        {editingFeatures.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <strong>Features:</strong> {editingFeatures.join(', ')}
                          </div>
                        )}
                        {Object.keys(editingConsumableCounts).length > 0 && (
                          <div>
                            <strong>Limits:</strong> {Object.entries(editingConsumableCounts).map(([feature, limit]) => `${feature}: ${limit}`).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Combined Features List */
                  (() => {
                    // Get all features from selected_features
                    const includedFeatures = selectedPurchase.purchased_plan?.selected_features || [];
                    
                    // Get consumable features with counts
                    const consumableFeatures = selectedPurchase.purchased_plan?.consumable_counts || {};
                    
                    // Create a combined list
                    const allFeatures = new Set<string>();
                    
                    // Add included features
                    includedFeatures.forEach(feature => allFeatures.add(feature));
                    
                    // Add consumable features
                    Object.keys(consumableFeatures).forEach(feature => allFeatures.add(feature));
                    
                    const featuresList = Array.from(allFeatures);
                    
                    if (featuresList.length > 0) {
                      return (
                        <div style={{
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          padding: '12px'
                        }}>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px'
                          }}>
                            {featuresList.map((feature: string, index: number) => {
                              const isConsumable = consumableFeatures[feature];
                              const count = isConsumable;
                              
                              return (
                                <div key={index} style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '8px 12px',
                                  backgroundColor: 'white',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '6px',
                                  transition: 'all 0.2s ease'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FontAwesomeIcon 
                                      icon={isConsumable ? faChartBar : faCheckCircle} 
                                      style={{ 
                                        color: isConsumable ? '#d97706' : '#059669', 
                                        fontSize: '14px' 
                                      }} 
                                    />
                                    <span style={{ 
                                      color: '#374151', 
                                      fontWeight: '500',
                                      fontSize: '13px'
                                    }}>
                                      {feature}
                                    </span>
                                  </div>
                                  {isConsumable && (
                                    <span style={{ 
                                      color: '#92400e', 
                                      fontWeight: '600',
                                      backgroundColor: '#fef3c7',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      border: '1px solid #fbbf24'
                                    }}>
                                      {count}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    
                    // Fallback to plan data if purchased_plan is not available
                    if (selectedPurchase.plan) {
                      return (
                        <div style={{ marginBottom: '16px' }}>
                          <h5 style={{
                            margin: '0 0 12px 0',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#475569',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Plan Information (Current)
                          </h5>
                          <div style={{
                            padding: '16px',
                            backgroundColor: '#fef3c7',
                            border: '1px solid #f59e0b',
                            borderRadius: '8px'
                          }}>
                            <div style={{ color: '#92400e', fontSize: '14px', marginBottom: '8px' }}>
                              <strong>Plan Name:</strong> {selectedPurchase.plan.name}
                            </div>
                            {selectedPurchase.plan.description && (
                              <div style={{ color: '#92400e', fontSize: '14px' }}>
                                <strong>Description:</strong> {selectedPurchase.plan.description}
                              </div>
                            )}
                            <div style={{ color: '#92400e', fontSize: '12px', marginTop: '8px', fontStyle: 'italic' }}>
                              Note: This purchase was made before plan snapshots were implemented. 
                              Current plan details are shown above.
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // No Features Message
                    return (
                      <div style={{
                        padding: '20px',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#94a3b8', fontSize: '24px', marginBottom: '8px' }} />
                        <div style={{ color: '#64748b', fontSize: '14px' }}>
                          This plan includes all basic features
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>

              {/* Subscription Details */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Subscription Details
                </h4>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Transaction ID:</span>
                    <span style={{ fontWeight: '500', color: '#1f2937' }}>{selectedPurchase.transaction_id}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Status:</span>
                    <span style={{ 
                      fontWeight: '500',
                      color: selectedPurchase.status === 'completed' ? '#059669' : '#6b7280'
                    }}>
                      {selectedPurchase.status.charAt(0).toUpperCase() + selectedPurchase.status.slice(1)}
                    </span>
                  </div>
                  {selectedPurchase.start_date && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Start Date:</span>
                      <span style={{ fontWeight: '500', color: '#1f2937' }}>{formatDate(selectedPurchase.start_date)}</span>
                    </div>
                  )}
                  {selectedPurchase.end_date && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>End Date:</span>
                      <span style={{ fontWeight: '500', color: '#1f2937' }}>{formatDate(selectedPurchase.end_date)}</span>
                    </div>
                  )}
                  {selectedPurchase.amount && selectedPurchase.currency && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Amount Paid:</span>
                      <span style={{ fontWeight: '600', color: '#059669' }}>
                        {selectedPurchase.currency} {selectedPurchase.amount}
                      </span>
                    </div>
                  )}
                  {selectedPurchase.duration && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Duration:</span>
                      <span style={{ fontWeight: '500', color: '#1f2937' }}>{selectedPurchase.duration}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Snapshot Information */}
              {selectedPurchase.purchased_plan?.snapshot_taken_at && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#6b7280',
                  textAlign: 'center'
                }}>
                  Plan snapshot taken on {formatDate(selectedPurchase.purchased_plan.snapshot_taken_at)}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div>
                {!isEditingSubscription && selectedPurchase?.purchased_plan && (
                  <button
                    onClick={handleEditSubscription}
                    disabled={loadingFeatures}
                    style={{
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      cursor: loadingFeatures ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s ease',
                      opacity: loadingFeatures ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!loadingFeatures) {
                        e.currentTarget.style.backgroundColor = '#2563eb';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!loadingFeatures) {
                        e.currentTarget.style.backgroundColor = '#3b82f6';
                      }
                    }}
                  >
                    {loadingFeatures ? 'Loading...' : 'Edit Subscription'}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                {isEditingSubscription ? (
                  <>
                    <button
                      onClick={handleCancelEdit}
                      disabled={updatingSubscription}
                      style={{
                        background: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        cursor: updatingSubscription ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s ease',
                        opacity: updatingSubscription ? 0.6 : 1
                      }}
                      onMouseOver={(e) => {
                        if (!updatingSubscription) {
                          e.currentTarget.style.backgroundColor = '#4b5563';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!updatingSubscription) {
                          e.currentTarget.style.backgroundColor = '#6b7280';
                        }
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveSubscription}
                      disabled={updatingSubscription}
                      style={{
                        background: '#059669',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        cursor: updatingSubscription ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s ease',
                        opacity: updatingSubscription ? 0.6 : 1
                      }}
                      onMouseOver={(e) => {
                        if (!updatingSubscription) {
                          e.currentTarget.style.backgroundColor = '#047857';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!updatingSubscription) {
                          e.currentTarget.style.backgroundColor = '#059669';
                        }
                      }}
                    >
                      {updatingSubscription ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleClosePlanDetailsModal}
                    style={{
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#4b5563';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#6b7280';
                    }}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPurchases;
